/**
 * Client-side image compression for attachment uploads.
 * Resizes big photos so they fit under our 5 MB attachment cap.
 */

const MAX_DIM = 2000;      // longest side, px
const JPEG_QUALITY = 0.82; // JPEG quality for re-encode

function readAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error("Could not read file"));
    r.readAsDataURL(file);
  });
}

function loadImage(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Could not decode image"));
    img.src = dataUrl;
  });
}

/**
 * Return a compressed data URL (JPEG for photos) plus its byte size.
 * Non-image files are returned as-is.
 */
export async function processAttachment(file) {
  const mime = file.type || "";
  const isImage = mime.startsWith("image/");
  const rawDataUrl = await readAsDataURL(file);

  if (!isImage) {
    return {
      name: file.name || "attachment",
      mime: mime || "application/octet-stream",
      size: file.size,
      data: rawDataUrl,
    };
  }

  const img = await loadImage(rawDataUrl);
  const longest = Math.max(img.width, img.height);
  const scale = longest > MAX_DIM ? MAX_DIM / longest : 1;
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, w, h);
  const outMime = mime === "image/png" && rawDataUrl.length < 1_500_000 ? "image/png" : "image/jpeg";
  const dataUrl = canvas.toDataURL(outMime, JPEG_QUALITY);

  // Approximate byte size of the base64 payload
  const base64Len = dataUrl.length - dataUrl.indexOf(",") - 1;
  const size = Math.floor(base64Len * 3 / 4);

  return {
    name: file.name || "photo.jpg",
    mime: outMime,
    size,
    data: dataUrl,
  };
}
